// LooksContactForm.js

import React, { useState, useEffect } from 'react';
import moment            from 'moment-timezone';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { saveContactSubmission } from '../utils/dbUtilities';
import { useAuth }               from '../contexts/AuthContext';

import './ContactForm.css';

const LooksContactForm = () => {
  /* ─────────── form state ─────────── */
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [message,  setMessage]  = useState('');
  const [math,     setMath]     = useState({ num1:0, num2:0 });
  const [answer,   setAnswer]   = useState('');

  const [isSubmitted,   setIsSubmitted]   = useState(false);
  const [statusMsg,     setStatusMsg]     = useState('');

  /* validation errors */
  const [nameErr,  setNameErr]  = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [msgErr,   setMsgErr]   = useState('');
  const [mathErr,  setMathErr]  = useState('');

  const { currentUser } = useAuth();
  const isSmallScreen   = useMediaQuery('(max-width:1180px)');

  /* ─────────── utils ─────────── */
  const username = currentUser
    ? (currentUser.username || currentUser.displayName || currentUser.email?.split('@')[0])
    : null;

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e).toLowerCase());

  const genMath = () =>
    setMath({ num1: Math.floor(Math.random()*10), num2: Math.floor(Math.random()*10) });

  /* ─────────── effects ─────────── */
  useEffect(() => { window.scrollTo(0,0); genMath(); }, []);

  /* ─────────── submit ─────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    let ok = true;
    if (!name.trim())         { setNameErr('Enter your name');   ok = false; } else setNameErr('');
    if (!isValidEmail(email)) { setEmailErr('Enter a valid email'); ok=false; } else setEmailErr('');
    if (!message.trim())      { setMsgErr('Enter a message');    ok = false; } else setMsgErr('');
    if (parseInt(answer,10)!==math.num1+math.num2){
      setMathErr('Wrong answer'); ok = false;
    } else setMathErr('');

    if (!ok) { genMath(); return; }

    const when = moment().tz('America/New_York').format('YYYY-MM-DD HH:mm:ss');
    try {
      await saveContactSubmission({ name, email, message, timestamp: when, username });
      setStatusMsg('Thanks! Your message has been sent.');
      setIsSubmitted(true);
      setName(''); setEmail(''); setMessage(''); setAnswer('');
    } catch (err) {
      console.error(err);
      setStatusMsg('Failed to send – try again later.');
    }
  };

  /* ─────────── render ─────────── */
  return (
    <Box className="contact-form-wrapper">
      <Container maxWidth="md">
        <Paper
          elevation={3}
          className="contact-card"
          sx={{
            px:{ xs:3, sm:6 }, py:{ xs:4, sm:5 },
            borderRadius:'16px',
            display:'flex',
            flexDirection:'column',
            alignItems:'center',
            gap:3
          }}
        >
          {/* header */}
          <Box sx={{ width:'100%', mb: isSmallScreen ? 2 : 3 }}>
            <Typography
              variant={isSmallScreen ? 'h5' : 'h4'}
              sx={{ fontWeight:700, color:'#1d1d1f' }}
            >
              Contact Looksaway
            </Typography>
          </Box>

          {/* form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width:'100%' }}>
            <TextField
              label="Name" fullWidth margin="normal"
              value={name} onChange={e=>setName(e.target.value)}
              error={!!nameErr} helperText={nameErr}
            />
            <TextField
              label="Email" fullWidth margin="normal"
              value={email} onChange={e=>setEmail(e.target.value)}
              error={!!emailErr} helperText={emailErr}
            />
            <TextField
              label="Message" multiline rows={4} fullWidth margin="normal"
              value={message} onChange={e=>setMessage(e.target.value)}
              error={!!msgErr} helperText={msgErr}
            />

            {!isSubmitted && (
              <Box sx={{ mt:2 }}>
                <Typography sx={{ mb:1 }}>
                  {`CAPTCHA: ${math.num1} + ${math.num2} = ?`}
                </Typography>
                <TextField
                  fullWidth
                  value={answer}
                  onChange={(e)=>setAnswer(e.target.value)}
                  error={!!mathErr}
                  helperText={mathErr}
                />
              </Box>
            )}

            {statusMsg && (
              <Typography
                align="center"
                sx={{ mt:3, color: statusMsg.startsWith('Thanks') ? 'green' : 'red' }}
              >
                {statusMsg}
              </Typography>
            )}

            <Box sx={{ display:'flex', justifyContent:'center', mt:4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  textTransform:'none',
                  fontWeight:600,
                  px:5,
                  background:'#007aff',
                  '&:hover':{ background:'#0060d1' }
                }}
              >
                Send
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LooksContactForm;

